import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { GalleryCard } from "@/components/cards/Cards";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { mediaUrl } from "@/lib/api";
import { plainText } from "@/lib/content";

export const GalleryLightbox = ({ items, gridClassName }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const activeItem = activeIndex === null ? null : items[activeIndex];
  const hasMultipleItems = items.length > 1;

  const move = (direction) => {
    setActiveIndex((index) => (index + direction + items.length) % items.length);
  };

  return (
    <>
      <div className={gridClassName} data-testid="gallery-grid">
        {items.map((item, index) => (
          <GalleryCard
            key={item.id || `${item.image}-${index}`}
            item={item}
            onOpen={() => setActiveIndex(index)}
            testId={`gallery-open-${item.id || index}`}
          />
        ))}
      </div>

      <Dialog open={activeIndex !== null} onOpenChange={(open) => !open && setActiveIndex(null)}>
        <DialogContent
          className="max-h-[92vh] max-w-6xl overflow-y-auto border-white/20 bg-navy p-4 sm:p-6"
          closeClassName="z-10 border border-white/40 bg-navy/90 p-1 text-white opacity-100 hover:bg-brand-orange hover:text-white focus:ring-white"
          closeTestId="gallery-lightbox-close-button"
          data-testid="gallery-lightbox"
        >
          <DialogTitle className="sr-only">
            {plainText(activeItem?.caption) || "Gallery image"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Use the close button, click outside, or press Escape to return to the gallery.
          </DialogDescription>

          {activeItem && (
            <div className="relative flex min-h-[50vh] items-center justify-center">
              <img
                src={mediaUrl(activeItem.image)}
                alt={plainText(activeItem.caption) || "Gallery image"}
                className="max-h-[72vh] w-full object-contain"
                data-testid="gallery-lightbox-image"
              />
              {hasMultipleItems && (
                <>
                  <Button
                    aria-label="Previous gallery image"
                    className="absolute left-0 top-1/2 -translate-y-1/2 rounded-sm border-white/40 bg-navy/80 text-white hover:bg-brand-orange"
                    data-testid="gallery-lightbox-previous-button"
                    onClick={() => move(-1)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    aria-label="Next gallery image"
                    className="absolute right-0 top-1/2 -translate-y-1/2 rounded-sm border-white/40 bg-navy/80 text-white hover:bg-brand-orange"
                    data-testid="gallery-lightbox-next-button"
                    onClick={() => move(1)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <ChevronRight />
                  </Button>
                </>
              )}
            </div>
          )}
          {activeItem?.caption && (
            <p className="pr-10 text-sm leading-relaxed text-white/75" data-testid="gallery-lightbox-caption">
              {plainText(activeItem.caption)}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GalleryLightbox;